import type {
  Placement,
  PlacementRequest,
  PlacementRequestFeeType,
  PlacementRequestPlacementType,
  PlacementSplitInput,
  PlacementSplitInputOwnerType,
} from "openapi/api/model"

type SplitFormRow = {
  owner_name: string
  owner_type: PlacementSplitInputOwnerType
  percentage: number
  owner_id?: string
  email?: string
}

type UpdatePlacementArgs = {
  // required by PlacementRequest
  job_candidate_id: string
  placement_type: PlacementRequestPlacementType

  fee_amount: number
  fee_type: PlacementRequestFeeType

  // optional
  salary?: number | null
  start_date?: string | null
  end_date?: string | null
  notes?: string | null

  // required
  splits: SplitFormRow[]
}

function sumPercent(splits: SplitFormRow[]): number {
  return splits.reduce((acc, s) => acc + (Number.isFinite(s.percentage) ? s.percentage : 0), 0)
}

function assertValidUpdate(args: UpdatePlacementArgs): void {
  if (!args.job_candidate_id) throw new Error("job_candidate_id is required")
  if (!args.placement_type) throw new Error("placement_type is required")

  if (!Number.isFinite(args.fee_amount)) throw new Error("fee_amount must be a number")
  if (args.fee_amount < 0) throw new Error("fee_amount must be >= 0")

  if (args.fee_type === "percentage") {
    if (args.fee_amount > 100) throw new Error("fee_amount must be <= 100 when fee_type=percentage")
    // Spec says salary is required when fee_type is percentage
    if (args.salary == null || !Number.isFinite(args.salary)) {
      throw new Error("salary is required when fee_type=percentage")
    }
  }

  if (!Array.isArray(args.splits) || args.splits.length < 1) throw new Error("splits must have at least 1 item")

  const cleaned = args.splits.map((s) => ({
    ...s,
    owner_name: (s.owner_name ?? "").trim(),
  }))

  if (cleaned.some((s) => !s.owner_name)) throw new Error("each split must have owner_name")

  const total = sumPercent(cleaned)
  // allow tiny float noise
  if (Math.abs(total - 100) > 0.0001) throw new Error(`split percentages must sum to 100 (got ${total})`)

  if (cleaned.some((s) => !Number.isFinite(s.percentage) || s.percentage < 0 || s.percentage > 100)) {
    throw new Error("each split percentage must be between 0 and 100")
  }
}

export function buildPlacementUpdateBody(args: UpdatePlacementArgs): PlacementRequest {
  assertValidUpdate(args)

  const splits: PlacementSplitInput[] = args.splits.map((s) => ({
    owner_id: s.owner_id,
    owner_name: s.owner_name.trim(),
    email: s.email,
    owner_type: s.owner_type,
    percentage: s.percentage,
  }))

  const body: PlacementRequest = {
    job_candidate_id: args.job_candidate_id,
    placement_type: args.placement_type,
    fee_amount: args.fee_amount,
    fee_type: args.fee_type,
    splits,
  }

  // only attach optionals if provided (keeps payload clean)
  if (args.salary !== undefined) body.salary = args.salary
  if (args.start_date !== undefined) body.start_date = args.start_date
  if (args.end_date !== undefined) body.end_date = args.end_date
  if (args.notes !== undefined) body.notes = args.notes

  return body
}

export type PlacementListRow = Placement & {
  placement_id?: string | null
  placement_created_at?: string | null
}

// Placement lists come from placement_full_overview_v1, whose DB id column is placement_id.
// Generated API types still expose id, so UI actions must resolve either shape before opening,
// updating, or deleting a placement.
export function getPlacementRecordId(placement?: Placement | PlacementListRow | null): string {
  const row = placement as PlacementListRow | null | undefined
  return row?.placement_id ?? placement?.id ?? ""
}

export function getPlacementCreatedAt(placement?: Placement | PlacementListRow | null): string | null {
  const row = placement as PlacementListRow | null | undefined
  return placement?.created_at ?? row?.placement_created_at ?? null
}

type PlacementRow = {
  placement_id: string
  placement_revenue_total: number
}

export function getOrganizationRevenue(rows?: PlacementRow[]): number {
  const seenPlacements = new Set<string>()

  if (!rows || !rows.length) return 0

  let totalRevenue = 0

  for (const row of rows) {
    if (seenPlacements.has(row.placement_id)) continue

    seenPlacements.add(row.placement_id)
    totalRevenue += row.placement_revenue_total
  }

  return totalRevenue
}

type RevenueRow = {
  placement_id: string
  placement_revenue_total: number
  company_name?: string | null
  candidate_name?: string | null
  placement_created_date?: string | null
}

export function getPlacementRevenueBreakdown(rows: RevenueRow[] | undefined) {
  if (!rows?.length) return []

  const byPlacement = new Map<
    string,
    { placement_id: string; label: string; value: number }
  >()

  for (const r of rows) {
    if (!r?.placement_id) continue
    if (byPlacement.has(r.placement_id)) continue // critical: dedupe split-expanded rows

    const labelParts = [
      r.company_name?.trim(),
      r.candidate_name?.trim(),
      r.placement_created_date?.trim(),
    ].filter(Boolean)

    byPlacement.set(r.placement_id, {
      placement_id: r.placement_id,
      label: labelParts.length ? labelParts.join(" • ") : r.placement_id,
      value: Number(r.placement_revenue_total ?? 0),
    })
  }

  return Array.from(byPlacement.values())
    .filter((x) => Number.isFinite(x.value) && x.value > 0)
    .sort((a, b) => b.value - a.value)
}



function startOfMonthUtc(now = new Date()): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0))
}

function startOfNextMonthUtc(now = new Date()): Date {
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1, 0, 0, 0, 0))
}

export function getPlacementsThisMonth(placements?: Placement[], now = new Date()) {
  const start = startOfMonthUtc(now).getTime()
  const end = startOfNextMonthUtc(now).getTime()

  return placements?.filter((p) => {
    const t = Date.parse(getPlacementCreatedAt(p) ?? "") // ISO -> ms
    return Number.isFinite(t) && t >= start && t < end
  })
}

export function countPlacementsThisMonth(placements?: Placement[], now = new Date()): number {
  return getPlacementsThisMonth(placements, now)?.length??0
}

export function placementRevenue(p: Placement): number {
  if (p.fee_type === "percentage") return (Number(p.salary ?? 0) * p.fee_amount) / 100
  return p.fee_amount
}

export function revenueMoM(placements?: Placement[], now = new Date()) {
  const startThis = startOfMonthUtc(now).getTime()
  const startNext = startOfNextMonthUtc(now).getTime()
  const startPrev = startOfMonthUtc(new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1))).getTime()

  let thisMonth = 0
  let prevMonth = 0

  if(!placements || !placements.length) return

  for (const p of placements) {
    /*
      The list comes from `placement_full_overview_v1`, whose columns are
      `placement_id` and `placement_created_at`; `created_at` is the single-row
      shape. Reading the wrong one here meant this loop skipped every row, so
      month-on-month was always zero against zero.
    */
    const t = Date.parse(getPlacementCreatedAt(p) ?? "")
    if (!Number.isFinite(t)) continue

    const r = placementRevenue(p)

    if (t >= startThis && t < startNext) thisMonth += r
    else if (t >= startPrev && t < startThis) prevMonth += r
  }

  const delta = thisMonth - prevMonth
  const pct = prevMonth === 0 ? null : delta / prevMonth

  return { thisMonth, prevMonth, delta, pct }
}
